import { useFormContext } from "react-hook-form";

import { RobotoButton } from "@/shared/foundation/primitives";

import {
  attentionCount,
  formStatusKind,
  type FormStatusKind,
} from "./formStatus";
import styles from "./ResourceFormActions.module.css";
import { type SubmitError } from "./submitError";

export interface ResourceFormActionsProps {
  /** The words on the submit. */
  submitLabel: string;
  /** What the status line says before the first submit. */
  idleHint: string;
  onCancel?: () => void;
  /**
   * Which submit the row is offering, for tests to tell two of them apart.
   * Written onto the status line and the button.
   */
  submitKind?: string;
  /** Why the last submission failed, shown with the submit it answers. */
  serverError?: SubmitError | null;
}

function statusText(
  kind: FormStatusKind,
  idleHint: string,
  needingAttention: number,
): string {
  switch (kind) {
    case "unsubmitted":
      return idleHint;
    case "clean":
      return "Everything checks out.";
    case "attention":
      return needingAttention === 1
        ? "1 field needs attention."
        : `${needingAttention} fields need attention.`;
  }
}

/**
 * The row a form's main column ends on: what still needs attention, a way
 * out, and the submit — with the last submission's failure directly above it.
 *
 * The submit is never disabled for validity — a person has to be able to ask
 * what is wrong — only while a submission is in flight.
 *
 * It reads the surrounding form's `formState` and nothing else, so it says
 * nothing about which form it is in. A form that wants a second button or a
 * status of its own composes its own row from `formStatus` rather than adding
 * a prop here.
 */
export function ResourceFormActions({
  submitLabel,
  idleHint,
  onCancel,
  submitKind,
  serverError = null,
}: ResourceFormActionsProps) {
  const { formState } = useFormContext();
  const needingAttention = attentionCount(formState.errors);
  const statusKind = formStatusKind(formState.isSubmitted, needingAttention);

  return (
    <div className={styles.actions} data-testid="resource-form-actions">
      {serverError !== null && (
        <p
          className={styles.serverError}
          data-testid="resource-form-server-error"
          data-error-kind={serverError.kind}
          role="alert"
        >
          {serverError.message}
        </p>
      )}
      <div className={styles.row}>
        <p
          className={styles.status}
          data-testid="resource-form-status"
          data-attention-count={needingAttention}
          data-status-kind={statusKind}
          data-status-submit-kind={submitKind}
          role="status"
        >
          {statusText(statusKind, idleHint, needingAttention)}
        </p>
        <div className={styles.buttons}>
          {onCancel !== undefined && (
            <RobotoButton variant="secondary" onClick={onCancel}>
              Cancel
            </RobotoButton>
          )}
          <RobotoButton
            type="submit"
            loading={formState.isSubmitting}
            data-testid="resource-form-submit"
            data-submit-kind={submitKind}
          >
            {submitLabel}
          </RobotoButton>
        </div>
      </div>
    </div>
  );
}
